// ============================================================================
// raidCurse — pure helpers for the 24h "Boss Curse" workspace block (Phase 4)
// ----------------------------------------------------------------------------
// The Firestore doc lives at raid_curses/{workspaceId} with shape:
//   { workspaceId, startedAt: iso, expiresAt: iso, breakCost: number }
//
// Everything here is pure so it can be unit-tested AND reused on the server
// (inside an executeSpin transaction) and the client (provider snapshot).
// ============================================================================

export interface RaidCurseDoc {
  workspaceId?: string
  startedAt?: string
  expiresAt?: string
  breakCost?: number
}

/**
 * True if `curse` represents an active, non-expired Boss Curse at `nowIso`.
 * Defensive: missing doc / missing expiry / unparseable timestamp → false.
 */
export const isCurseActive = (
  curse: RaidCurseDoc | undefined | null,
  nowIso: string,
): boolean => {
  if (!curse?.expiresAt) return false
  const expiresAt = Date.parse(curse.expiresAt)
  const now = Date.parse(nowIso)
  if (Number.isNaN(expiresAt) || Number.isNaN(now)) return false
  return expiresAt > now
}

/** Standardized error so UI/service can recognize the curse path. */
export const RAID_CURSE_ERROR_CODE = 'RAID_CURSE_ACTIVE' as const

export class RaidCurseActiveError extends Error {
  readonly code = RAID_CURSE_ERROR_CODE
  constructor(message = '⚔️ Workspace is afflicted by a Boss Curse — spins and ticket grants are frozen for 24 hours.') {
    super(message)
    this.name = 'RaidCurseActiveError'
  }
}

export const isRaidCurseActiveError = (error: unknown): error is RaidCurseActiveError =>
  error instanceof Error && (error as { code?: string }).code === RAID_CURSE_ERROR_CODE
